import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';

interface Payment {
  id: number;
  case_id: number;
  client_name: string;
  file_reference: string;
  case_type: string;
  team_name: string;
  total_case_value: number;
  payment_type: string | null;
  invoice_date: string | null;
  invoice_month: string | null;
  payment_date: string | null;
  amount: number;
  vat_applied: boolean;
  vat_rate: number;
  payment_method: string | null;
  payment_status: string;
  notes: string | null;
}

interface CaseOption {
  id: number;
  file_reference: string | null;
  client_name: string;
  case_type: string | null;
}

interface PaymentForm {
  case_id: string;
  payment_type: string;
  invoice_date: string;
  invoice_month: string;
  payment_date: string;
  amount: string;
  vat_applied: boolean;
  vat_rate: string;
  payment_status: string;
  payment_method: string;
  notes: string;
}

const BLANK_FORM: PaymentForm = {
  case_id: '', payment_type: '', invoice_date: '', invoice_month: '',
  payment_date: '', amount: '', vat_applied: false, vat_rate: '20',
  payment_status: 'Not Paid', payment_method: '', notes: '',
};

function formatCurrency(value: number): string {
  return '£' + value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusBadgeClass(s: string): string {
  if (s === 'Paid') return 'badge badge-green';
  if (s === 'Partial') return 'badge badge-yellow';
  return 'badge badge-red';
}

function vatTotal(amount: string, vatApplied: boolean, vatRate: string): { subtotal: number; vat: number; total: number } {
  const sub = parseFloat(amount) || 0;
  const rate = vatApplied ? (parseFloat(vatRate) || 20) / 100 : 0;
  const vat = sub * rate;
  return { subtotal: sub, vat, total: sub + vat };
}

export default function PaymentsPage() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [cases, setCases] = useState<CaseOption[]>([]);
  const [form, setForm] = useState<PaymentForm>(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  function fetchPayments() {
    const url = filter ? `/api/payments?status=${encodeURIComponent(filter)}` : '/api/payments';
    apiFetch(url)
      .then(r => r.json())
      .then((d: Payment[]) => { setPayments(d); setLoading(false); })
      .catch(() => setLoading(false));
  }

  function fetchCases() {
    // /api/cases returns an array directly
    apiFetch('/api/cases')
      .then(r => r.json())
      .then((d: CaseOption[]) => {
        setCases(Array.isArray(d) ? d : []);
      })
      .catch(() => {});
  }

  useEffect(() => { fetchPayments(); }, [filter]);
  useEffect(() => { fetchCases(); }, []);

  function openEdit(p: Payment) {
    setEditingPayment(p);
    setAddingNew(false);
    setForm({
      case_id: String(p.case_id),
      payment_type: p.payment_type || '',
      invoice_date: p.invoice_date || '',
      invoice_month: p.invoice_month || '',
      payment_date: p.payment_date || '',
      amount: String(p.amount),
      vat_applied: p.vat_applied ?? false,
      vat_rate: String(p.vat_rate ?? 20),
      payment_status: p.payment_status,
      payment_method: p.payment_method || '',
      notes: p.notes || '',
    });
  }

  function openAdd() { setEditingPayment(null); setAddingNew(true); setForm(BLANK_FORM); }
  function closeModal() { setEditingPayment(null); setAddingNew(false); }
  function setField(k: keyof PaymentForm, v: string | boolean) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    setSaving(true);
    try {
      const { subtotal: _sub, total } = vatTotal(form.amount, form.vat_applied, form.vat_rate);
      const payload = {
        payment_type: form.payment_type || null,
        invoice_date: form.invoice_date || null,
        invoice_month: form.invoice_month || null,
        payment_date: form.payment_date || null,
        amount: total,
        vat_applied: form.vat_applied,
        vat_rate: parseFloat(form.vat_rate) || 20,
        payment_status: form.payment_status,
        payment_method: form.payment_method || null,
        notes: form.notes || null,
      };

      if (addingNew) {
        if (!form.case_id) { alert('Please select a case.'); setSaving(false); return; }
        await apiFetch('/api/payments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, case_id: parseInt(form.case_id) }),
        });
      } else if (editingPayment) {
        await apiFetch(`/api/payments/${editingPayment.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      closeModal();
      fetchPayments();
    } finally { setSaving(false); }
  }

  const vatTotals = vatTotal(form.amount, form.vat_applied, form.vat_rate);
  const totalBilled = payments.reduce((s, p) => s + p.amount, 0);
  const totalCollected = payments.filter(p => p.payment_date).reduce((s, p) => s + p.amount, 0);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  const isOpen = editingPayment !== null || addingNew;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Payments</h1>
          <p className="page-subtitle">Track invoices and payment collections</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Payment
        </button>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-label">Total Billed</div>
          <div className="stat-value">{formatCurrency(totalBilled)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Collected</div>
          <div className="stat-value">{formatCurrency(totalCollected)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Outstanding</div>
          <div className="stat-value" style={{ color: 'var(--danger)' }}>{formatCurrency(totalBilled - totalCollected)}</div>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 20 }}>
        {[{ key: '', label: 'All' }, { key: 'Not Paid', label: 'Not Paid' }, { key: 'Partial', label: 'Partial' }, { key: 'Paid', label: 'Paid' }].map(f => (
          <button key={f.key} className={`tab${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
      </div>

      <div className="card">
        <div className="table-container">
          {payments.length === 0 ? (
            <div className="empty-state"><p>No payments found.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>File Ref</th>
                  <th>Case Type</th>
                  <th>Invoice Month</th>
                  <th>Amount (incl. VAT)</th>
                  <th>VAT</th>
                  <th>Status</th>
                  <th>Payment Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const rate = p.vat_applied ? (p.vat_rate ?? 20) / 100 : 0;
                  const subtotal = p.vat_applied ? p.amount / (1 + rate) : p.amount;
                  const vatAmt = p.amount - subtotal;
                  return (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.client_name}</td>
                      <td>{p.file_reference || '-'}</td>
                      <td>{p.case_type || '-'}</td>
                      <td>{p.invoice_month || '-'}</td>
                      <td>
                        <span title={p.vat_applied ? `Subtotal: ${formatCurrency(subtotal)} + VAT: ${formatCurrency(vatAmt)}` : undefined}>
                          {formatCurrency(p.amount)}
                        </span>
                      </td>
                      <td>
                        {p.vat_applied
                          ? <span className="badge badge-blue">+{p.vat_rate ?? 20}%</span>
                          : <span className="badge badge-gray">No VAT</span>}
                      </td>
                      <td><span className={statusBadgeClass(p.payment_status)}>{p.payment_status}</span></td>
                      <td>{p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-GB') : '-'}</td>
                      <td>
                        <button className="btn-icon" onClick={() => openEdit(p)}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{addingNew ? 'Add Payment' : 'Edit Payment'}</h2>
                {editingPayment && (
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                    {editingPayment.client_name} · {editingPayment.file_reference || `Case #${editingPayment.case_id}`}
                  </p>
                )}
              </div>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1 }}>✕</button>
            </div>
            <div className="modal-body">
              {addingNew && (
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Case *</label>
                    <select className="form-input" value={form.case_id} onChange={e => setField('case_id', e.target.value)}>
                      <option value="">— Select a case —</option>
                      {cases.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.file_reference || `#${c.id}`} — {c.client_name}{c.case_type ? ` (${c.case_type})` : ''}
                        </option>
                      ))}
                    </select>
                    {cases.length === 0 && (
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>No cases found. Create a case first.</p>
                    )}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payment Type</label>
                  <input className="form-input" type="text" placeholder="e.g. Fixed Fee, Instalment" value={form.payment_type} onChange={e => setField('payment_type', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Invoice Month</label>
                  <input className="form-input" type="month" value={form.invoice_month} onChange={e => setField('invoice_month', e.target.value)} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Sub-total Amount (£)</label>
                  <input className="form-input" type="number" step="0.01" min="0" placeholder="0.00" value={form.amount} onChange={e => setField('amount', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">VAT</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                      <input type="checkbox" checked={form.vat_applied} onChange={e => setField('vat_applied', e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--primary)', cursor: 'pointer' }} />
                      Apply VAT ({form.vat_rate}%)
                    </label>
                  </div>
                </div>
              </div>

              {form.vat_applied && form.amount && (
                <div style={{ background: '#F9FAFB', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Sub-total</span>
                    <span>{formatCurrency(vatTotals.subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>VAT ({form.vat_rate}%)</span>
                    <span>{formatCurrency(vatTotals.vat)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 2 }}>
                    <span>Total (incl. VAT)</span>
                    <span style={{ color: 'var(--primary)' }}>{formatCurrency(vatTotals.total)}</span>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-input" value={form.payment_status} onChange={e => setField('payment_status', e.target.value)}>
                    <option value="Not Paid">Not Paid</option>
                    <option value="Partial">Partial</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Date</label>
                  <input className="form-input" type="date" value={form.payment_date} onChange={e => setField('payment_date', e.target.value)} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <input className="form-input" type="text" placeholder="e.g. Bank Transfer, Card" value={form.payment_method} onChange={e => setField('payment_method', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Invoice Date</label>
                  <input className="form-input" type="date" value={form.invoice_date} onChange={e => setField('invoice_date', e.target.value)} />
                </div>
              </div>

              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={form.notes} onChange={e => setField('notes', e.target.value)} rows={3} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
